import React, {useCallback, useContext, useEffect, useState, useRef} from 'react';
import {List} from 'immutable';
import {Link, withRouter, WithRouterProps} from 'react-router';
import classNames from 'classnames';

import {history} from 'client/history';
import {Button, Card, Dropdown, DropdownItem, Icon, IconButton, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import useOnClickOutside from 'lib/hooks/useOnClickOutside';

import {MobilePracticeViewContext} from 'components/PracticeView/MobilePracticeViewProvider.context';

import SubmissionResetWarningModal from './SubmissionResetWarningModal.react';

function isEmptyOrSpaces(str) {
  return str === null || str.match(/^ *$/) !== null;
}

function getDisplayName(student) {
  if (!student) {
    return 'Loading...';
  }
  let displayName = `${student!.getFirstName()} ${student!.getLastName()}`;
  if (isEmptyOrSpaces(displayName)) {
    displayName = student!.getEmail();
  }
  return displayName;
}

interface Student {
  getId: () => string;
  getFirstName: () => string;
  getLastName: () => string;
  getEmail: () => string;
}

const getStudentsQuery = (assignmentId, classroomId) => {
  const query = resource('assignments_v3')
    .mandarkEndpoint(['assignments_v3', assignmentId])
    .include('students_v2')
    .filter({
      included: {
        students_v2: {
          classrooms_v1: classroomId
        }
      }
    });
  return query;
};

interface Props extends WithRouterProps {
  assignment: any;
}

const ViewingStudentSubmissionToolbar = withRouter(({params, location, assignment}: Props) => {
  const {classroomId} = params;
  const {isMobile} = useContext(MobilePracticeViewContext);
  const {assignmentId, studentId, questionId} = params;
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [showSubmissionDropdown, setShowSubmissionDropdown] = useState(false);
  const [students, setStudents] = useState<List<Student>>(List());
  const [currentStudent, setCurrentStudent] = useState<Student | undefined>(undefined);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const studentDropdownWrapperRef = useRef<HTMLDivElement>(null);
  const submissionDropdownWrapperRef = useRef<HTMLDivElement>(null);
  const studentToggleRef = useRef<HTMLDivElement>(null);
  const submissionToggleRef = useRef<HTMLDivElement>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [assignmentSubmissionStatusCanBeReset, setAssignmentSubmissionStatusCanBeReset] =
    useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const correctCount = assignment?.getMeta?.().getStudentCountOfCorrectGuesses();
  const totalCount = assignment?.getMeta?.().getCountOfQuestions();

  useOnClickOutside(
    studentDropdownWrapperRef,
    useCallback(
      () => !!showStudentDropdown && setShowStudentDropdown(false),
      [showStudentDropdown, setShowStudentDropdown]
    )
  );

  useOnClickOutside(
    submissionDropdownWrapperRef,
    useCallback(
      () => !!showSubmissionDropdown && setShowSubmissionDropdown(false),
      [showSubmissionDropdown, setShowSubmissionDropdown]
    )
  );

  useEffect(() => {
    let subscribed = true;
    const fetchStudents = async () => {
      const query = getStudentsQuery(assignmentId, classroomId);
      try {
        const response = await query.getResourcePromise();
        const studentsSorted = response.getStudents().sortBy((student) => {
          return student.getLastName().toUpperCase();
        });
        if (subscribed) {
          setStudents(studentsSorted);
        }
      } catch (error) {
        history.pushState(null, '/error');
      }
    };
    fetchStudents();
    return () => {
      subscribed = false;
    };
  }, [assignmentId, classroomId]);

  useEffect(() => {
    const newCurrentStudentIndex = students.findIndex((student) => student?.getId() === studentId);
    setCurrentStudentIndex(newCurrentStudentIndex);

    const currentStudentFiltered = students
      .filter((student) => student!.getId() === studentId)
      .get(0);
    if (currentStudentFiltered) {
      setCurrentStudent(currentStudentFiltered);
    }
  }, [students, studentId]);

  useEffect(() => {
    if (assignment?.getMeta?.()) {
      const currentIsSubmitted = assignment.getMeta().isStudentSubmitted();
      setIsSubmitted(currentIsSubmitted);
      const currentAssignmentSubmissionStatusCanBeReset =
        currentIsSubmitted &&
        assignment.getMeta().getStudentCountOfGuesses() <
          assignment.getMeta().getCountOfQuestions();
      setAssignmentSubmissionStatusCanBeReset(currentAssignmentSubmissionStatusCanBeReset);
    }
  }, [assignment, studentId]);

  return (
    <>
      <div
        style={{paddingBottom: '4px', paddingTop: '4px'}}
        className={classNames(
          'u-border-b u-border-color_slate-200 u-display_flex u-align-items_center u-justify-content_space-between u-white-space_nowrap',
          {
            'u-overflow-x_scroll': isMobile
          }
        )}
      >
        <div className='u-display_flex u-align-items_center u-pad-lr_1 practice-view-toolbar__content'>
          <IconButton
            as={Link}
            to={() => {
              if (currentStudentIndex <= 0) {
                // if first item, then link to current page
                return location.pathname;
              }
              return `/classes/${classroomId}/assignments/${assignmentId}/student-detail/${students
                .get(currentStudentIndex - 1)
                .getId()}/question/${questionId}/view`;
            }}
            disabled={currentStudentIndex <= 0}
            size='s'
            variant='outlined'
            icon='chevron-left'
            label='Previous Student'
          />
          <Button
            className='u-justify-content_space-between u-mar-l_1 u-mar-r_1'
            variant='outlined'
            style={{minWidth: '18ch'}}
            ref={studentToggleRef}
            onClick={() => setShowStudentDropdown(!showStudentDropdown)}
            size='s'
          >
            <span>{getDisplayName(currentStudent)}</span>
            <Icon icon='caret-down' className='u-mar-l_1' />
          </Button>
          {/* eslint-disable-next-line */}
          <div onClick={(e) => e.stopPropagation()} ref={studentDropdownWrapperRef}>
            {showStudentDropdown ? (
              <Dropdown.Tray
                positionFixed
                placement='bottom-start'
                target={studentToggleRef.current}
                class='vss-student-dropdown'
              >
                <div className='vss-student-dropdown__wrapper'>
                  <Card className='u-overflow_hidden'>
                    {students.map((student, index) => {
                      const displayName = getDisplayName(student);
                      return (
                        student && (
                          <DropdownItem
                            key={student.getId()}
                            as={Link}
                            to={`/classes/${classroomId}/assignments/${assignmentId}/student-detail/${student.getId()}/question/${questionId}/view`}
                            onClick={() => setShowStudentDropdown(false)}
                            preventDefaultClickHandler={false}
                            checked={currentStudentIndex === index}
                          >
                            {displayName}
                          </DropdownItem>
                        )
                      );
                    })}
                  </Card>
                </div>
              </Dropdown.Tray>
            ) : null}
          </div>
          <IconButton
            as={Link}
            to={() => {
              if (currentStudentIndex + 1 >= students.size) {
                // if last item, then link to current page
                return location.pathname;
              }
              return `/classes/${classroomId}/assignments/${assignmentId}/student-detail/${students
                .get(currentStudentIndex + 1)
                .getId()}/question/${questionId}/view`;
            }}
            disabled={currentStudentIndex + 1 >= students.size}
            size='s'
            label='Next Student'
            icon='chevron-right'
            variant='outlined'
          />
        </div>
        <div className='u-display_flex u-align-items_center u-pad-lr_1 practice-view-toolbar__content'>
          <span className='a-text--size-m u-mar-r_2'>
            Assignment Total: {correctCount}/{totalCount}
          </span>
          <Button
            className='u-mar-r_1'
            variant='outlined'
            ref={submissionToggleRef}
            onClick={() => setShowSubmissionDropdown(!showSubmissionDropdown)}
            size='s'
            color={isSubmitted ? 'green' : 'secondary'}
            disabled={!assignmentSubmissionStatusCanBeReset}
          >
            {isSubmitted ? 'Submitted' : 'Not Submitted'}
            <Icon icon='caret-down' className='u-mar-l_1' />
          </Button>
          {/* eslint-disable-next-line */}
          <div onClick={(e) => e.stopPropagation()} ref={submissionDropdownWrapperRef}>
            {showSubmissionDropdown ? (
              <Dropdown.Tray
                positionFixed
                placement='bottom-start'
                target={submissionToggleRef.current}
                class='vss-student-dropdown'
              >
                <div className='vss-student-dropdown__wrapper'>
                  <Card className='u-overflow_hidden'>
                    <DropdownItem
                      as={Button}
                      onClick={() => {
                        setShowSubmissionDropdown(false);
                      }}
                      preventDefaultClickHandler={false}
                      checked={isSubmitted}
                      className='u-cursor_not-allowed u-width_100pc'
                    >
                      <Text>Submitted</Text>
                    </DropdownItem>
                    <DropdownItem
                      as={Button}
                      onClick={() => {
                        setShowWarningModal(true);
                      }}
                      preventDefaultClickHandler={false}
                      checked={!isSubmitted}
                    >
                      <Text>Not Submitted</Text>
                    </DropdownItem>
                  </Card>
                </div>
              </Dropdown.Tray>
            ) : null}
          </div>
          <Button
            as={Link}
            to={`/classes/${classroomId}/assignments/${assignmentId}/student-detail/${studentId}`}
            size='s'
          >
            View Report
          </Button>
        </div>
      </div>
      {showWarningModal ? (
        <SubmissionResetWarningModal
          student={currentStudent}
          assignment={assignment}
          onClose={() => setShowWarningModal(false)}
        />
      ) : null}
    </>
  );
});

export default ViewingStudentSubmissionToolbar;
